import { DatePicker, Empty, Layout } from 'antd';
import FormItem from 'antd/lib/form/FormItem';
import dayjs, { type Dayjs } from 'dayjs';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { DriverTileListSkeleton } from '@/components/skeletons/DriverTileListSkeleton';
import type { GetDriversWithLastTrackPointQuery } from '@/generated/graphql/graphql';
import { DriverDrawerView } from './components/DriverDrawer/DriverDrawerView';
import { DriverTileListView } from './components/DriverTileListView';
import { TrackingMapView } from './components/TrackingMapView';
import { POLLING_INTERVAL_MS } from './constants';
import { useGetDriverOrderChanges, useGetDriversWithLastTrackPoint } from './query/tracking.hooks';
import { getDayRange } from './utils/dateRange';

const { Content, Sider } = Layout;

interface SelectedOrder {
  id: number;
  lat: number;
  lng: number;
}

interface GeoEventHighlight {
  lat: number;
  lng: number;
  statusName: string;
}

const EXPRESS_ONLY_STORAGE_KEY = 'tracking-show-express-only';
const TODAY_KEY_REFRESH_MS = 60_000;

function useTodayKey(): string {
  const [todayKey, setTodayKey] = useState(() => dayjs().format('YYYY-MM-DD'));
  useEffect(() => {
    const interval = setInterval(() => {
      const next = dayjs().format('YYYY-MM-DD');
      setTodayKey((prev) => (prev === next ? prev : next));
    }, TODAY_KEY_REFRESH_MS);
    return () => clearInterval(interval);
  }, []);
  return todayKey;
}

function readShowExpressOnlyFromStorage(): boolean {
  try {
    const saved = localStorage.getItem(EXPRESS_ONLY_STORAGE_KEY);
    return saved ? Boolean(JSON.parse(saved)) : false;
  } catch {
    return false;
  }
}

export const TrackingPageView: React.FC = () => {
  const { selectedDepartmentId: departmentId } = useDepartments();
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [showExpressOnly, setShowExpressOnly] = useState<boolean>(readShowExpressOnlyFromStorage);

  const { dateFrom, dateTo } = useMemo(() => getDayRange(selectedDate), [selectedDate]);
  const dateKey = selectedDate.format('YYYY-MM-DD');
  const todayKey = useTodayKey();
  const isToday = dateKey === todayKey;
  const runtimeOptions = useMemo(
    () => ({ refetchInterval: isToday ? POLLING_INTERVAL_MS : (false as const) }),
    [isToday]
  );

  const {
    data: driversWithLastTrackPoint,
    isLoading,
    isPending,
  } = useGetDriversWithLastTrackPoint(
    {
      departmentId,
      dateFrom,
      dateTo,
    },
    runtimeOptions
  );

  const [selectedDriver, setSelectedDriver] = useState<
    GetDriversWithLastTrackPointQuery['user'][0] | null
  >(null);
  const [selectedOrder, setSelectedOrder] = useState<SelectedOrder | null>(null);
  const [geoEventHighlight, setGeoEventHighlight] = useState<GeoEventHighlight | null>(null);

  const { data: orderChangesData } = useGetDriverOrderChanges(
    {
      driverId: selectedDriver?.id ?? '',
      dateFrom,
      dateTo,
    },
    runtimeOptions
  );

  const prevDriverIdRef = useRef<string | undefined>(undefined);
  const prevDateKeyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const currentDriverId = selectedDriver?.id;
    const driverChanged =
      prevDriverIdRef.current !== undefined && prevDriverIdRef.current !== currentDriverId;
    const dateChanged = prevDateKeyRef.current !== undefined && prevDateKeyRef.current !== dateKey;

    if (driverChanged || dateChanged) {
      setSelectedOrder(null);
      setGeoEventHighlight(null);
    }
    prevDriverIdRef.current = currentDriverId;
    prevDateKeyRef.current = dateKey;
  }, [selectedDriver?.id, dateKey]);

  const handleSelectOrder = useCallback((order: SelectedOrder | null) => {
    setSelectedOrder(order);
    setGeoEventHighlight(null);
  }, []);

  const handleGeoEventNavigate = useCallback(
    (coords: { lat: number; lng: number }, statusName: string, orderId: number) => {
      setGeoEventHighlight({ ...coords, statusName });
      setSelectedOrder({ id: orderId, ...coords });
    },
    []
  );

  const handleDriverSelect = useCallback(
    (
      driverOrUpdater:
        | GetDriversWithLastTrackPointQuery['user'][0]
        | null
        | ((
            prev: GetDriversWithLastTrackPointQuery['user'][0] | null
          ) => GetDriversWithLastTrackPointQuery['user'][0] | null)
    ) => {
      setSelectedDriver(driverOrUpdater);
    },
    []
  );

  const handleDriverSelectFromMap = useCallback(
    (driverId: string) => {
      if (!driversWithLastTrackPoint) return;
      const driver = driversWithLastTrackPoint.user.find((d) => d.id === driverId);
      if (driver) {
        handleDriverSelect(driver);
      }
    },
    [driversWithLastTrackPoint, handleDriverSelect]
  );

  const handleCloseDriverDrawer = useCallback(() => {
    setSelectedDriver(null);
    setSelectedOrder(null);
    setGeoEventHighlight(null);
  }, []);

  const orderChanges = orderChangesData?.order_change;

  return (
    <Layout style={{ height: 'calc(100vh - 32px)', overflow: 'hidden' }}>
      <Sider theme="light" width={390} style={{ height: '100%', overflow: 'auto' }}>
        <FormItem className="app-form-item" style={{ padding: 8 }}>
          <DatePicker
            value={selectedDate}
            allowClear={false}
            onChange={(date) => {
              if (date) {
                setSelectedDate(date);
              }
            }}
          />
        </FormItem>
        {isLoading || isPending || !driversWithLastTrackPoint ? (
          <DriverTileListSkeleton />
        ) : driversWithLastTrackPoint.user.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No drivers found for the selected date"
            style={{ marginTop: 48 }}
          />
        ) : (
          <DriverTileListView
            selectedDriverId={selectedDriver?.id}
            onSelect={handleDriverSelect}
            data={driversWithLastTrackPoint}
            showExpressOnly={showExpressOnly}
            onExpressFilterChange={setShowExpressOnly}
          />
        )}
      </Sider>
      <Content style={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            overflow: 'hidden',
            width: '100%',
            height: '100%',
          }}
        >
          <DriverDrawerView
            close={handleCloseDriverDrawer}
            selectedDriver={selectedDriver}
            selectedDate={selectedDate}
            onOrderSelect={handleSelectOrder}
            onGeoEventNavigate={handleGeoEventNavigate}
            selectedOrderId={selectedOrder?.id}
            orderChanges={orderChanges}
            isToday={isToday}
          />
        </div>
        <TrackingMapView
          selectedDriverId={selectedDriver?.id}
          selectedDate={selectedDate}
          drivers={driversWithLastTrackPoint}
          showExpressOnly={showExpressOnly}
          selectedOrder={selectedOrder}
          geoEventHighlight={geoEventHighlight}
          orderChanges={orderChanges}
          onDriverSelect={handleDriverSelectFromMap}
          onOrderSelect={handleSelectOrder}
          isToday={isToday}
        />
      </Content>
    </Layout>
  );
};
